import React, { useState, useEffect } from 'react';
import currencyService from '../services/currencyService';
import { useAuth } from '../contexts/AuthContext';
import './Admin.css';
import './ConnectorForm.css';

// Admin › Platform › Currencies — the currency table (V580) that every currency picker reads.
// Anyone signed in may view the list; adding and editing are superadmin only (the admin API enforces it,
// this screen just does not offer them). A code is never renamed — partners, chains and hotels store it —
// so a code is retired by marking it inactive, which hides it from new selections only.

const EMPTY = { code: '', description: '', billing: 'N', active: 'Y' };

const YesNo = ({ value, onChange }) => (
  <input type="checkbox" checked={value === 'Y'} onChange={e => onChange(e.target.checked ? 'Y' : 'N')} />
);

const Currencies = () => {
  const { isSuperAdmin } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newRow, setNewRow] = useState(EMPTY);
  const [editCode, setEditCode] = useState(null);
  const [editRow, setEditRow] = useState(EMPTY);

  const load = async () => {
    try {
      setLoading(true);
      setCurrencies(await currencyService.listAll());
      setError(null);
    } catch (err) {
      setError('Failed to load currencies: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newRow.code.trim() || !newRow.description.trim()) return;
    try {
      await currencyService.create(newRow);
      setNewRow(EMPTY);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add currency');
    }
  };

  const startEdit = (c) => {
    setEditCode(c.code);
    setEditRow({ code: c.code, description: c.description || '', billing: c.billing, active: c.active });
  };

  const saveEdit = async () => {
    try {
      const { description, billing, active } = editRow;
      await currencyService.update(editCode, { description, billing, active });
      setEditCode(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update currency');
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Currencies</h2>
      </div>
      <p style={{ color: '#6b7280', marginTop: '-0.5rem', marginBottom: '1rem' }}>
        The currencies offered for a partner, chain or hotel. <strong>Billing</strong> currencies are the ones
        HCONNECT charges in (Stripe prices, marketplace API cost). An inactive currency is hidden from new
        selections; records already using it keep it.
      </p>

      {error && <div className="error">{error}</div>}

      {isSuperAdmin && (
        <div className="inline-add-row">
          <div className="field">
            <label>Code</label>
            <input type="text" maxLength={3} value={newRow.code} onChange={e => setNewRow(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="e.g. FJD" />
          </div>
          <div className="field grow">
            <label>Description</label>
            <input type="text" value={newRow.description} onChange={e => setNewRow(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Fijian Dollar" />
          </div>
          <div className="field">
            <label>Billing</label>
            <YesNo value={newRow.billing} onChange={v => setNewRow(p => ({ ...p, billing: v }))} />
          </div>
          <button type="button" className="btn-primary" onClick={add}>Add</button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>Code</th><th>Description</th><th>Billing</th><th>Active</th>{isSuperAdmin && <th>Actions</th>}</tr>
          </thead>
          <tbody>
            {currencies.length === 0 ? (
              <tr><td colSpan={isSuperAdmin ? 5 : 4} style={{ textAlign: 'center' }}>No currencies</td></tr>
            ) : currencies.map(c => (
              <tr key={c.code}>
                {editCode === c.code ? (
                  <>
                    <td style={{ fontWeight: 600 }}>{c.code}</td>
                    <td><input value={editRow.description} onChange={e => setEditRow(p => ({ ...p, description: e.target.value }))} /></td>
                    <td><YesNo value={editRow.billing} onChange={v => setEditRow(p => ({ ...p, billing: v }))} /></td>
                    <td><YesNo value={editRow.active} onChange={v => setEditRow(p => ({ ...p, active: v }))} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn-small btn-primary" onClick={saveEdit}>Save</button>
                      <button type="button" className="btn-small" style={{ marginLeft: 4 }} onClick={() => setEditCode(null)}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ fontWeight: 600 }}>{c.code}</td>
                    <td>{c.description}</td>
                    <td>{c.billing === 'Y' ? 'Yes' : '-'}</td>
                    <td>{c.active === 'Y' ? 'Yes' : 'No'}</td>
                    {isSuperAdmin && (
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button type="button" className="btn-small" onClick={() => startEdit(c)} title="Edit">✏️</button>
                      </td>
                    )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Currencies;
